import {
  gToKg,
  kgToG,
  m3ToMm3,
  mm3ToM3,
} from 'src/components/ShipmentOrderLegRunSplitForm/splitAllocation';
import { ProductEntity } from 'src/entities/ProductEntity';
import {
  ShipmentOrderItemsEntity,
  ShipmentOrderLegEntity,
  ShipmentOrderLegRunEntity,
  ShipmentOrderLegRunItemPayload,
} from 'src/entities/ShipmentOrderResponse.entity';
import { computeAutoFillItemWeightVolume } from 'src/utils/autoFillItemWeightVolume';

export type SplitCellValue = {
  quantity: number | '';
  weightKg: number | '';
  volumeM3: number | '';
  weightManuallyEdited: boolean;
  volumeManuallyEdited: boolean;
};

// legRunId -> itemKey -> 値。itemKey は既存商品なら String(item.id)、新規なら clientKey
export type SplitItemValues = Record<number, Record<string, SplitCellValue>>;

let clientKeySeq = 0;
export const nextClientKey = (): string => {
  clientKeySeq += 1;
  return `ck-${Date.now()}-${clientKeySeq}`;
};

export const itemKeyOf = (it: { id?: number; clientKey?: string }): string =>
  (it.id != null ? String(it.id) : it.clientKey ?? '');

export const findSplitLeg = (legs: ShipmentOrderLegEntity[]): ShipmentOrderLegEntity | null =>
  legs.find((leg) => leg.shipmentOrderLegRuns.some((run) => run.wasSplit)) ?? null;

export const splitRunsOf = (leg: ShipmentOrderLegEntity): ShipmentOrderLegRunEntity[] =>
  leg.shipmentOrderLegRuns
    .filter((run) => run.wasSplit)
    .slice()
    .sort((a, b) => a.sequenceNumber - b.sequenceNumber);

const emptyCell = (): SplitCellValue => ({
  quantity: '', weightKg: '', volumeM3: '', weightManuallyEdited: false, volumeManuallyEdited: false,
});

export const buildInitialSplitItemValues = (
  runs: ShipmentOrderLegRunEntity[],
  items: ShipmentOrderItemsEntity[],
): SplitItemValues => {
  const result: SplitItemValues = {};
  runs.forEach((run) => {
    const byItem: Record<string, SplitCellValue> = {};
    items.forEach((it) => {
      const alloc = run.items.find((a) => a.shipmentOrderItemId === it.id);
      byItem[itemKeyOf(it)] = alloc
        ? {
          quantity: alloc.quantity ?? '',
          weightKg: alloc.weightG == null ? '' : gToKg(alloc.weightG) ?? '',
          volumeM3: alloc.volumeMm3 == null ? '' : mm3ToM3(alloc.volumeMm3) ?? '',
          weightManuallyEdited: false,
          volumeManuallyEdited: false,
        }
        : emptyCell();
    });
    result[run.id] = byItem;
  });
  return result;
};

export const sumForItem = (
  values: SplitItemValues,
  runIds: number[],
  itemKey: string,
): { quantity: number; weightKg: number; volumeM3: number } => {
  // 表示桁ずれ防止のため base 単位（g / mm³）で合算してから換算する
  let quantity = 0;
  let weightG = 0;
  let volumeMm3 = 0;
  runIds.forEach((runId) => {
    const cell = values[runId]?.[itemKey];
    if (!cell) return;
    quantity += Number(cell.quantity) || 0;
    weightG += kgToG(Number(cell.weightKg) || 0);
    volumeMm3 += m3ToMm3(Number(cell.volumeM3) || 0);
  });
  return { quantity, weightKg: gToKg(weightG) ?? 0, volumeM3: mm3ToM3(volumeMm3) ?? 0 };
};

export const updateSplitValue = (
  values: SplitItemValues,
  runId: number,
  itemKey: string,
  field: 'quantity' | 'weightKg' | 'volumeM3',
  value: number | '',
  entity: ProductEntity | null,
): SplitItemValues => {
  const cell = { ...(values[runId]?.[itemKey] ?? emptyCell()) };
  cell[field] = value;
  if (field === 'weightKg') cell.weightManuallyEdited = value !== '';
  if (field === 'volumeM3') cell.volumeManuallyEdited = value !== '';
  if (field === 'quantity' && value !== '' && entity) {
    // 数量変更時、未編集の重量/体積を既存の自動補完ロジック（単価 × 数量）で補完
    const autofill = computeAutoFillItemWeightVolume({
      entity,
      quantity: Number(value),
      weightKgManuallyEdited: cell.weightManuallyEdited,
      volumeM3ManuallyEdited: cell.volumeManuallyEdited,
    });
    if (autofill.weightKg !== undefined) cell.weightKg = autofill.weightKg;
    if (autofill.volumeM3 !== undefined) cell.volumeM3 = autofill.volumeM3;
  }
  return { ...values, [runId]: { ...values[runId], [itemKey]: cell } };
};

export const addItemToSplitValues = (
  values: SplitItemValues,
  runIds: number[],
  clientKey: string,
): SplitItemValues => {
  const next: SplitItemValues = { ...values };
  runIds.forEach((runId) => {
    next[runId] = { ...next[runId], [clientKey]: emptyCell() };
  });
  return next;
};

export const removeItemFromSplitValues = (
  values: SplitItemValues,
  itemKey: string,
): SplitItemValues => {
  const next: SplitItemValues = {};
  Object.entries(values).forEach(([runId, byItem]) => {
    const { [itemKey]: _removed, ...rest } = byItem;
    next[Number(runId)] = rest;
  });
  return next;
};

export const buildLegRunItemsPayload = (
  values: SplitItemValues,
  runId: number,
  items: ShipmentOrderItemsEntity[],
): ShipmentOrderLegRunItemPayload[] =>
  items.map((it) => {
    const cell = values[runId]?.[itemKeyOf(it)];
    return {
      shipmentOrderItemId: it.id,
      clientKey: it.id != null ? undefined : it.clientKey,
      quantity: cell ? Number(cell.quantity) || 0 : 0,
      weightG: cell ? kgToG(Number(cell.weightKg) || 0) : 0,
      volumeMm3: !cell || cell.volumeM3 === '' ? null : m3ToMm3(Number(cell.volumeM3) || 0),
    };
  });
